import type { HelpCategory } from "../../types";

export const crm: HelpCategory = {
  id: "crm",
  title: "Клиенты, маркетинг и каналы продаж",
  description: "База гостей, долги, акции, SMS, сайт и QR-меню, доставка, услуги и банкеты.",
  icon: "users",
  articles: [
    {
      id: "customers",
      title: "Клиенты: база гостей",
      summary: "Карточка гостя с историей заказов и любимыми блюдами, сегменты, теги, объединение дублей и удаление персональных данных.",
      path: "/customers",
      feature: "core.customers",
      permission: "customers.view",
      keywords: ["клиент", "гость", "база", "crm", "телефон", "сегмент", "тег", "дубль"],
      related: ["debts", "sms", "promotions"],
      body: [
        { t: "video", id: "customers", title: "Клиенты и карточка гостя", duration: 29 },
        {
          t: "steps", title: "Добавить клиента",
          items: [
            "[«Клиенты»](/customers) → **«Новый клиент»**: имя и телефон в формате +996… (номер уникален), день рождения, теги.",
            "**Согласие на рассылки** — без него SMS гостю не уйдут.",
            "На кассе клиента можно найти по последним цифрам телефона и прикрепить к заказу.",
          ],
        },
        {
          t: "list", title: "Что видно в карточке",
          items: ["Сумма покупок, число визитов и частота, средний чек.", "Топ-5 любимых блюд.", "История заказов и долги.", "Сегмент (новый, постоянный, уходящий…) — считается сам по визитам."],
        },
        {
          t: "list", title: "Служебные действия",
          items: [
            "**Объединить** — если один гость заведён дважды: история и долги переедут в одну карточку.",
            "**Удалить персональные данные** — по просьбе гостя: имя и телефон стираются, история продаж для отчётов остаётся обезличенной.",
          ],
        },
      ],
    },
    {
      id: "debts",
      title: "Долги гостей: продажа в долг и погашение",
      summary: "Кредитный лимит гостю, оплата заказа «в долг» на кассе, погашение в кассе, корректировки, списание и отчёт по просрочке.",
      path: "/debts",
      feature: "core.debts",
      permission: "debts.view",
      keywords: ["долг", "в долг", "кредит", "лимит", "погашение", "должник"],
      related: ["customers", "pos-basics", "cash-shifts"],
      body: [
        { t: "video", id: "debt", title: "Продажа в долг и погашение", duration: 41 },
        {
          t: "steps", title: "Включить продажу в долг",
          items: ["В карточке клиента включите **«Разрешить продажу в долг»** и задайте **кредитный лимит**.", "На кассе выберите клиента в заказе и способ оплаты **«В долг»**. Касса не даст превысить лимит."],
        },
        {
          t: "steps", title: "Погашение",
          items: ["[«Долги»](/debts) или карточка клиента → **«Погасить»**.", "Наличные проходят через открытую кассовую смену.", "Погашение закрывает самые старые долги первыми."],
        },
        { t: "p", text: "**Корректировка** — исправить сумму долга с причиной. **Списание** — простить безнадёжный долг. Всё попадает в журнал аудита. Вкладка «Мы должны поставщикам» — долги перед поставщиками, см. [[suppliers]]." },
      ],
    },
    {
      id: "promotions",
      title: "Акции и промокоды",
      summary: "Скидки по правилам: счастливые часы, комбо, «1+1», подарок к заказу, промокоды. Касса применяет лучшую акцию сама.",
      path: "/promotions",
      feature: "addon.promotions_pro",
      permission: "promotions.view",
      keywords: ["акция", "промокод", "happy hour", "счастливые часы", "комбо", "1+1", "подарок"],
      related: ["pos-discounts", "sms"],
      body: [
        {
          t: "steps", title: "Создать акцию",
          items: [
            "[«Акции»](/promotions) → **«Новая акция»**: название и механика — скидка %, скидка суммой, комбо, «N+M» (второй в подарок), подарок.",
            "На что действует: все позиции, категории или конкретные блюда.",
            "Условия: дни недели и часы (можно через полночь), период дат, филиалы, тип заказа, первый заказ гостя, тег клиента, минимальная сумма.",
            "**Промокод** — акция сработает только с кодом (уникален в компании). Лимиты: сколько раз всего и на гостя.",
            "**Эксклюзивная** — применяется одна, без других. Остальные складываются по группам совместимости: в группе гость получает самую выгодную.",
          ],
        },
        { t: "p", text: "На кассе кнопка **«Акции»** включает пересчёт для заказа и принимает промокоды. Ручная скидка и акции суммируются, но не больше подытога." },
        { t: "tip", text: "Отчёт по акциям: сколько раз сработала и во сколько обошлась — вкладка «Отчёт» в разделе." },
      ],
    },
    {
      id: "sms",
      title: "SMS-рассылки",
      summary: "Сообщения гостям по сегменту или тегу: акции, новинки, поздравления. Только тем, кто дал согласие. Квота SMS в месяц.",
      path: "/sms",
      feature: "addon.sms",
      permission: "sms.view",
      keywords: ["sms", "смс", "рассылка", "сообщение", "день рождения"],
      related: ["customers", "promotions"],
      body: [
        {
          t: "steps",
          items: [
            "[«SMS-рассылки»](/sms) → **«Новая рассылка»**.",
            "Аудитория: сегмент, тег или «день рождения в ближайшие 7 дней». Видно, сколько получателей.",
            "Текст: система считает **части SMS** (кириллица — 70 символов в части) и сколько квоты уйдёт.",
            "Отправьте **тестовое SMS** себе, затем — рассылку.",
          ],
        },
        { t: "warn", text: "Квота — число частей SMS в месяц (тариф + пакеты «SMS 1000»). На 80% и 100% придёт предупреждение; без остатка рассылка не запустится." },
      ],
    },
    {
      id: "site-qr",
      title: "Сайт заведения и QR-меню",
      summary: "Публичный сайт с меню и филиалами, QR-коды на столы, заказ гостей с телефона, свой адрес сайта и свой бренд.",
      path: "/settings/site",
      feature: "core.qr_menu",
      permission: "settings.view",
      keywords: ["сайт", "qr", "qr-меню", "домен", "адрес сайта", "бренд", "логотип", "заказ с телефона"],
      related: ["tables-halls", "menu-items", "subscription"],
      body: [
        { t: "video", id: "qr-order", title: "Гость заказывает по QR-коду", duration: 29 },
        {
          t: "list", title: "Что есть",
          items: [
            "**Сайт** {адрес}.cafeos.kg: главная, меню по филиалам, страница блюда, адреса и часы работы. Позиции с галочкой «Видна гостям».",
            "**QR-меню**: наклейка на столе → меню на телефоне гостя.",
            "**Заказ по QR** (дополнение): гость заказывает сам, заказ появляется на кассе и уходит на кухню; кнопки «Позвать официанта» и «Счёт».",
          ],
        },
        {
          t: "steps", title: "Настроить",
          items: [
            "[«Сайт и QR»](/settings/site): заголовок, описание, фото, SEO-заголовок и описание для поиска.",
            "**«Контакты и соцсети»**: телефон, email, Instagram, WhatsApp, Telegram, TikTok, Facebook, YouTube — появятся внизу сайта и на главной. Пустые поля гости не видят.",
            "Включите **«Заказ гостей через QR»** (если подключён).",
            "Напечатайте **QR-коды столов** и QR для стойки.",
          ],
        },
        {
          t: "steps", title: "Свой адрес сайта (дополнение)",
          items: [
            "Укажите домен, например menu.mycafe.kg.",
            "У регистратора домена добавьте TXT-запись `_cafeos.<домен>` с кодом проверки и CNAME на адрес, который покажет система.",
            "Нажмите **«Проверить»**. HTTPS-сертификат выпустится сам в течение нескольких минут.",
          ],
        },
        { t: "tip", text: "Дополнение «Свой бренд» заменяет Coca вашим названием, логотипом и цветом — в программе, на сайте и в письмах. Настраивается в [«Компании»](/settings/company)." },
      ],
    },
    {
      id: "delivery",
      title: "Доставка и курьеры",
      summary: "Зоны доставки на карте, оформление доставки, доска статусов с таймерами, курьеры, наличные у курьеров и отчёт.",
      path: "/deliveries",
      feature: "addon.delivery",
      permission: "deliveries.view",
      keywords: ["доставка", "курьер", "зона доставки", "адрес"],
      related: ["pos-basics", "users-roles"],
      body: [
        {
          t: "steps", title: "Настроить",
          items: [
            "Вкладка «Зоны»: нарисуйте полигон или задайте радиус; минимальный заказ, стоимость доставки, «бесплатно от», время доставки.",
            "Добавьте курьеров: пользователи с ролью **«Курьер»** (см. [[users-roles]]).",
          ],
        },
        {
          t: "steps", title: "Работа",
          items: [
            "Создайте заказ типа «Доставка» на кассе и оформите доставку: адрес, телефон, оплата (предоплата, наличными, картой курьеру). Адрес вне зон не принимается.",
            "Доска: Новые → Готовятся → Готово → Курьер назначен → В пути → Доставлено. Таймер показывает опоздание к обещанному времени.",
            "Курьер видит «Мои доставки» на телефоне и меняет статус.",
            "Вернувшись, курьер сдаёт наличные: **«Принять наличные»** — деньги попадут в смену кассы.",
          ],
        },
      ],
    },
    {
      id: "services",
      title: "Услуги и аренда (караоке, кальян, бильярд)",
      summary: "Почасовые услуги с таймером на сервере, комнаты с минимальным заказом и комиссии сотрудникам.",
      path: "/services",
      feature: "addon.services",
      permission: "services.view",
      keywords: ["услуга", "аренда", "караоке", "кальян", "бильярд", "таймер", "почасовая", "комната", "комиссия"],
      related: ["menu-items", "payroll"],
      body: [
        {
          t: "steps",
          items: [
            "Создайте позицию меню типа **«Услуга»** и в разделе [«Услуги»](/services) настройте тарифик: за штуку или **почасово** (цена часа, шаг округления, минимум, ступени, цена по расписанию).",
            "Добавьте **комнаты** с минимальным заказом (если есть).",
            "Задайте **комиссию** сотруднику: фиксированная, % или смешанная.",
            "Старт услуги — таймер идёт на сервере, паузы учитываются. Стоп — услуга сама добавится в заказ стола; если не добрали минимальный заказ, добавится доплата.",
          ],
        },
      ],
    },
    {
      id: "banquets",
      title: "Банкеты и мероприятия",
      summary: "От заявки до закрытия: меню на гостя, депозиты, бронь зала, коммерческое предложение и заказ в день мероприятия.",
      path: "/banquets",
      feature: "addon.banquets",
      permission: "banquets.view",
      keywords: ["банкет", "свадьба", "корпоратив", "мероприятие", "депозит", "предоплата"],
      related: ["reservations", "cash-shifts"],
      body: [
        {
          t: "steps",
          items: [
            "[«Банкеты»](/banquets) → **«Новый банкет»**: дата, время, гости, залы, контакт.",
            "Меню: позиции на гостя или на весь стол, по курсам. Видны сумма, себестоимость и маржа; «Продукты под банкет» — сколько закупить.",
            "Статусы: заявка → предварительно → подтверждён. Для подтверждения нужен **депозит** не меньше требуемого.",
            "Подтверждённый банкет блокирует залы для броней.",
            "В день мероприятия — **«Начать банкет»**: создаётся заказ, депозит зачитывается в оплату. Завершить можно после полной оплаты.",
          ],
        },
        { t: "tip", text: "Депозит наличными принимается только при открытой кассовой смене. Незачтённый депозит можно вернуть. Печать коммерческого предложения на A4 — в карточке банкета." },
      ],
    },
  ],
};
